import { useRef, useEffect, useState } from "react";
import { Container, Typography, Stack, Divider, Box } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import numberToWords from "number-to-words";
import { useNavigate, useLocation } from "react-router";
import dayjs from "dayjs";
import QrCodeGenerator from "views/commonComponents/QrCode/QrCodeGenerator";
import { useSelector } from "react-redux";

const A4Print = () => {
  const componentRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, tableData, qrData, typ, navigateBack } = location.state;
  const company = useSelector((state) => state.common.companyInfo);
  const companyLogo = company.logo ? JSON.parse(company.logo)?.img : null;
  const decimalCount = useSelector((state) => state.common.decimalCount);
  const primaryCurrency = useSelector((state) => state.common.primaryCurrency);
  const subCurrency = useSelector((state) => state.common.subCurrency);
  const isQrShow = useSelector((state) => state.common.QrShowPrint);
  const commonState = useSelector((state) => state.common);
  const [sections, setSections] = useState([]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      navigate(navigateBack);
    },
  });

  useEffect(() => {
    if (company) {
      const timeoutId = setTimeout(() => {
        handlePrint();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    if (tableData && tableData.length > 0) {
      const generatedSections = generateSections(tableData);
      setSections(generatedSections);
    }
  }, [tableData]);

  function convertNumberToWordsWithCurrency(number) {
    const paisaAmount = (number * 100) % 100;
    const words = numberToWords.toWords(Math.floor(number));
    const paisaInWords = numberToWords.toWords(paisaAmount);
    return `${words} ${primaryCurrency} and  ${paisaInWords} ${subCurrency}`;
  }

  const amount = data.BillNetTotalField;
  const result = convertNumberToWordsWithCurrency(amount);

  const calculateItemsPerPage = (items) => {
    // Constants for A4 page measurements
    const A4_HEIGHT_CM = 29.7;
    const HEADER_HEIGHT_CM = 8.0;
    const TABLE_HEADER_HEIGHT_CM = 1.5;
    const FOOTER_HEIGHT_CM = 8.0;

    const availableSpace =
      A4_HEIGHT_CM -
      HEADER_HEIGHT_CM -
      TABLE_HEADER_HEIGHT_CM -
      FOOTER_HEIGHT_CM;

    let currentHeight = 0;
    let itemsPerPage = 0;

    // Base row height for items without long descriptions
    const baseRowHeight = 0.7; // cm

    for (const item of items) {
      // Estimate height based on description length
      const description = item.ItemNameTextField || "";
      // More accurate line count estimation for print
      const lineCount = Math.ceil(description.length / 35);
      const itemHeight = baseRowHeight * Math.max(1, lineCount);

      if (currentHeight + itemHeight > availableSpace && itemsPerPage > 0) {
        break;
      }

      currentHeight += itemHeight;
      itemsPerPage++;
    }

    return Math.max(1, itemsPerPage);
  };

  const generateSections = (tableData) => {
    const sections = [];

    // Assign unique IDs to each product
    const updatedTableData = tableData.map((item, index) => ({
      ...item,
      id: index + 1,
    }));

    let currentIndex = 0;
    let pageNumber = 1;
    const totalItems = updatedTableData.length;

    while (currentIndex < totalItems) {
      // Calculate how many items can fit on this page
      const remainingItems = updatedTableData.slice(currentIndex);
      const itemsThisPage = calculateItemsPerPage(remainingItems);

      const sectionItems = updatedTableData.slice(
        currentIndex,
        currentIndex + itemsThisPage
      );

      sections.push(
        renderSection(
          sectionItems,
          pageNumber,
          Math.ceil(totalItems / itemsThisPage)
        )
      );

      currentIndex += itemsThisPage;
      pageNumber++;
    }

    return sections;
  };

  const renderSection = (sectionItems, currentPage, totalPages) => {
    return (
      <div
        key={`section-${currentPage}`}
        style={{
          marginTop: currentPage !== 1 ? "1rem" : "0",
          pageBreakBefore: currentPage !== 1 ? "always" : "auto",
          color: "black",
          borderColor: "black",
          width: "100%",
          padding: "2px",
        }}
      >
        <div style={{ border: "1px solid black" }}>
          <Stack direction="row" sx={{ height: "15.6rem" }}>
            <div
              style={{
                width: "45%",
                borderRight: "1px solid black",
                height: "15.6rem",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-around",
                overflow: "hidden",
              }}
            >
              <div
                style={{ width: "13.3rem", height: "30%", margin: "1rem" }}
                onClick={() => {
                  navigate("/sales/orderForm");
                }}
              >
                <img
                  src={companyLogo}
                  style={{
                    objectFit: "contain",
                    height: "100%",
                    width: "100%",
                  }}
                  alt="Company Logo"
                />
              </div>

              <Container
                disableGutters
                sx={{ padding: "0.3rem", color: "black" }}
              >
                <Typography variant="h4">{company.Name}</Typography>
                <Typography>{company.Add1}</Typography>
                <Typography>{(company.city_name, company.Place)}</Typography>
                <Typography> {company.Country}</Typography>
                <Typography> Zipcode{company.Postal}</Typography>
                <Typography>
                  Ph : {company.Ph} | VAT: {company.Add2}
                </Typography>

                <Typography> {company.Web}</Typography>
              </Container>
            </div>
            <Stack direction="column" sx={{ width: "55%", height: "15.6rem" }}>
              <Typography align="center" variant="h4" sx={{ height: "1.5rem" }}>
                {typ === "SB"
                  ? "Tax Invoice"
                  : typ === "OF"
                  ? "Order Form"
                  : "Sales Return"}
              </Typography>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  borderColor: "black",
                }}
              >
                <tbody style={{ padding: "10px", borderColor: "black" }}>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                        height: "1.5rem",
                      }}
                    >
                      <Typography>
                        {data.BillNo
                          ? typ === "OF"
                            ? `Voucher No: ${data.NewBillNo}`
                            : typ === "B2C"
                            ? `Invoice No: ${data.prefix}`
                            : `Invoice No: ${data.NewBillNo}`
                          : `Return No: ${data.RetNo}`}
                      </Typography>
                    </td>
                    <td
                      style={{
                        height: "1.5rem",
                        padding: 0,
                        border: "1px solid black",
                      }}
                    >
                      <div style={{ display: "flex", height: "100%" }}>
                        <Typography
                          sx={{
                            width: "80%",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "12px",
                            p: 0.2,
                          }}
                        >
                          Original for Recipient
                        </Typography>
                        <div
                          style={{
                            width: "20%",
                            height: "100%",
                            borderLeft: "1px solid black",
                          }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                        height: "1.5rem",
                      }}
                    >
                      <Typography>
                        {typ === "OF" ? `Voucher Date:` : ` Invoice Date:`}
                        <b>{dayjs(data.BillDate).format("DD-MM-YYYY")}</b>
                      </Typography>
                    </td>
                    <td
                      style={{
                        height: "1.5rem",
                        padding: 0,
                        border: "1px solid black",
                      }}
                    >
                      <div style={{ display: "flex", height: "100%" }}>
                        <Typography
                          sx={{
                            width: "80%",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "12px",
                            p: 0.2,
                          }}
                        >
                          Duplicate for Transporter
                        </Typography>
                        <div
                          style={{
                            width: "20%",
                            height: "100%",
                            borderLeft: "1px solid black",
                          }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                      }}
                    >
                      <Typography>
                        State Code: <b>0</b>
                      </Typography>
                    </td>
                    <td
                      style={{
                        height: "1.5rem",
                        padding: 0,
                        border: "1px solid black",
                      }}
                    >
                      <div style={{ display: "flex", height: "100%" }}>
                        <Typography
                          sx={{
                            width: "80%",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "12px",
                            p: 0.2,
                          }}
                        >
                          Triplicate for Supplier
                        </Typography>
                        <div
                          style={{
                            width: "20%",
                            height: "100%",
                            borderLeft: "1px solid black",
                          }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td
                      style={{
                        border: "1px solid black",
                      }}
                    >
                      <Typography>
                        Client ID: <b>0</b>
                      </Typography>
                    </td>
                    <td
                      style={{
                        border: "1px solid black",
                      }}
                    >
                      <Typography>
                        Vehicle No: <b>0</b>
                      </Typography>
                    </td>
                  </tr>
                </tbody>
              </table>
              <Stack
                direction="row"
                justifyContent="space-between"
                spacing={0}
                sx={{ height: "100%", p: 1 }}
              >
                <Stack direction="column" alignItems={"start"} width={"80%"}>
                  <Typography>
                    <b style={{ display: "block" }}>To :{data.BillPartyName}</b>{" "}
                    {data.CashP}
                  </Typography>
                  <Typography>{data.warranty} </Typography>
                  <Typography>PH: {data.Ph} </Typography>
                  <Typography>
                    <b>VAT NO:</b> {data.vat_no}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="center" width={"20%"}>
                  <Box width={120}>
                    {isQrShow ? (
                      <QrCodeGenerator
                        qrData={data.zatca_einv_qr_code || qrData}
                      />
                    ) : null}
                  </Box>
                </Stack>
              </Stack>
            </Stack>
          </Stack>

          <div style={{ height: "50rem" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    SI NO
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      border: "1px solid black",
                      width: "30%",
                      padding: "0.6rem",
                    }}
                  >
                    Description
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Qty
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Rate
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Unit
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Amount
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Tax %
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Tax amt
                  </th>

                  <th
                    style={{
                      textAlign: "right",
                      border: "1px solid black",
                      padding: "0.6rem",
                    }}
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {sectionItems.map((prod, index) => (
                  <tr key={index}>
                    <td
                      style={{
                        textAlign: "center",
                        alignContent: "start",
                        borderRight: "1px solid black",
                      }}
                    >
                      {prod.id}
                    </td>
                    <td
                      style={{
                        textAlign: "left",
                        borderRight: "1px solid black",
                        paddingLeft: "5px",
                      }}
                    >
                      {prod.ItemNameTextField}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                      }}
                    >
                      {prod.qty}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                      }}
                    >
                      {parseFloat(prod.Rate1).toFixed(decimalCount)}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                      }}
                    >
                      {prod.Typ}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                      }}
                    >
                      {prod.Rate1 * prod.qty.toFixed(decimalCount)}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                      }}
                    >
                      {prod.Tax1}
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        borderRight: "1px solid black",
                        alignContent: "start",
                      }}
                    >
                      {parseFloat(prod.taxAmt).toFixed(decimalCount)}
                    </td>

                    <td
                      style={{
                        textAlign: "right",
                        borderRight: "1px solid black",
                        padding: "5px",
                        alignContent: "start",
                      }}
                    >
                      {prod.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Divider sx={{ borderBottom: "0.5px solid black" }} />
          </div>

          <Stack direction="row">
            <Stack
              direction="column"
              sx={{
                width: "60%",
                borderRight: "1px solid black",
              }}
            >
              <Container
                disableGutters
                sx={{ padding: "0.3rem", borderTop: "1px solid black" }}
              >
                <Typography> Amount In Words :</Typography>
                <Typography variant="h5" sx={{ textTransform: "capitalize" }}>
                  {result}
                </Typography>
              </Container>
              <Container
                disableGutters
                sx={{ borderTop: "1px solid black", padding: "0.3rem" }}
              >
                <Typography> Bank Details:-</Typography>
                <Typography variant="h5"> Bank Name: {company.bank}</Typography>
                <Typography variant="h5">
                  {" "}
                  A/c Name: {company.beneficiary}
                </Typography>
                <Typography variant="h5">
                  {" "}
                  A/c No: {company.account_no}
                </Typography>
                <Typography variant="h5"> Branch: {company.branch}</Typography>
                <Typography variant="h5"> IFSC: {company.ifsc_code}</Typography>
              </Container>
              <Container
                disableGutters
                sx={{
                  borderTop: "1px solid black",
                  padding: "0.8rem 0.3rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  height: "100%",
                }}
              >
                <Typography>Declaration</Typography>
                <div
                  style={{ textOverflow: "ellipsis" }}
                  dangerouslySetInnerHTML={{
                    __html: commonState.salesBillDeclaration,
                  }}
                />
              </Container>
            </Stack>
            <Stack direction="column" sx={{ width: "40%" }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderTop: "1px solid black",
                  height: "2rem",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                }}
              >
                <Typography variant="h5">Subtotal :</Typography>
                <Typography variant="h5">
                  {" "}
                  {data.BillTotalField.toFixed(decimalCount)}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderTop: "1px solid black",
                  height: "2rem",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                }}
              >
                <Typography variant="h5"> Tax Amount: </Typography>
                <Typography variant="h5">
                  {" "}
                  {typ !== "SR" || typ !== "SRB2C"
                    ? parseFloat(data?.TItTaxAmt + data?.BillTaxField).toFixed(
                        decimalCount
                      )
                    : parseFloat(data.BillTaxField).toFixed(decimalCount)}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderTop: "1px solid black",
                  height: "2rem",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                }}
              >
                <Typography variant="h5"> Discount : </Typography>
                <Typography variant="h5">
                  {data.TDiscAmt + data.BillDiscAmtField}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  borderBlock: "1px solid black",
                  height: "2rem",
                  paddingLeft: "5px",
                  paddingRight: "5px",
                }}
              >
                <Typography variant="h5"> Net Total : </Typography>
                <Typography variant="h5">
                  {parseFloat(data.BillNetTotalField).toFixed(decimalCount)}
                </Typography>
              </Stack>
              <Stack
                direction="column"
                justifyContent="space-between"
                sx={{ height: "5rem", paddingLeft: "5px", paddingRight: "5px" }}
              >
                <Typography
                  align="center"
                  variant="h6"
                  sx={{ marginLeft: "auto" }}
                >
                  E&OE
                </Typography>
                <Typography align="center" variant="h6">
                  For {company.Name}
                </Typography>
                <Typography align="center" variant="h6">
                  Authorised Signatory
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </div>
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            marginTop: "2px",
          }}
        >
          Page {currentPage} of {totalPages}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div
        ref={componentRef}
        style={{
          background: "white",
          margin: "1rem",
          height: "auto",
        }}
      >
        {sections}
      </div>
    </div>
  );
};

export default A4Print;
